import classNames from 'classnames';
import { Heading } from 'components/ui/Heading/Heading';
import { useFlysiteUrls } from 'hooks/useFlysiteUrls/useFlysiteUrls';
import type { Error as ErrorType } from 'models';
import { type ConnectedProps, connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getCurrentLocale, getInvalidParamErrors } from 'selectors';
import { hasDuplicatedPaymentParamError } from 'selectors/errors/errors';
import { useTranslations } from 'utils';
import { createMarkup } from 'utils/createMarkup/createMarkup';
import { ReactComponent as ErrorIcon } from '../../../src/icons/error.svg';

type BaseErrorProps = {
  className?: string;
  fieldsWithErrors?: ErrorType[];
  locale?: string;
};

type ErrorDefaultProps = BaseErrorProps & {
  errorKey?: string;
};

type ErrorWrapperProps = ConnectedProps<typeof connector> & {
  className?: string;
  errorKey?: string;
};

const ErrorDefault = ({
  className,
  fieldsWithErrors = [],
  errorKey = 'checkout.error.message',
}: ErrorDefaultProps) => {
  const i18n = useTranslations();
  const { contactUrl } = useFlysiteUrls();

  const errorMessage = i18n.t(errorKey, { contactUrl });

  const errorDetail = (field: ErrorType) => (
    <li className="fontSize-sm type-regular margin-0" key={field.param}>
      {field.param} {field.message}
    </li>
  );

  const hasFieldsWithErrors = !!fieldsWithErrors.length;

  return (
    <div
      className={classNames('textAlign-center paddingBottom-xxxxl', className)}
    >
      <ErrorIcon className="marginBottom-lg marginTop-xxxxl" />
      <Heading
        as="h1"
        size="large"
        className="textAlign-center marginBotom-sm fontWeight-semiBold marginBottom-sm"
      >
        {i18n.t('checkout.error.title')}
      </Heading>
      <p
        className="type-regular paddingH-xxl"
        dangerouslySetInnerHTML={createMarkup(errorMessage)}
      />
      {hasFieldsWithErrors && (
        <>
          <span>{i18n.t('checkout.error.errors')}</span>
          <ul className="unstyled-list">{fieldsWithErrors.map(errorDetail)}</ul>
        </>
      )}
    </div>
  );
};

const ErrorDuplicatedPayment = ({
  className,
  fieldsWithErrors = [],
}: BaseErrorProps) => {
  const i18n = useTranslations();
  const { contactUrl } = useFlysiteUrls();
  const duplicatedPaymentMessage = i18n.t(
    'checkout.error.india_ongoing_payment',
    { contactUrl },
  );

  const errorDetail = (field: ErrorType) => (
    <li className="fontSize-sm type-regular margin-0" key={field.param}>
      {field.param} {field.message}
    </li>
  );

  const hasFieldsWithErrors = !!fieldsWithErrors.length;

  return (
    <div
      className={classNames('textAlign-center paddingBottom-xxxxl', className)}
    >
      <ErrorIcon className="marginBottom-lg marginTop-xxxxl" />
      <Heading
        as="h1"
        size="large"
        className="textAlign-center marginBotom-sm fontWeight-semiBold marginBottom-sm"
      >
        This payment can't be completed
      </Heading>
      <p
        className="type-regular paddingH-xxl"
        dangerouslySetInnerHTML={createMarkup(duplicatedPaymentMessage)}
      />
      {hasFieldsWithErrors && (
        <>
          <span>{i18n.t('checkout.error.errors')}</span>
          <ul className="unstyled-list">{fieldsWithErrors.map(errorDetail)}</ul>
        </>
      )}
    </div>
  );
};

const ErrorWrapper = ({
  className,
  locale,
  errorKey = 'checkout.error.message',
  fieldsWithErrors = [],
  hasDuplicatedPaymentError = false,
}: ErrorWrapperProps) => {
  if (hasDuplicatedPaymentError) {
    return (
      <ErrorDuplicatedPayment
        className={className}
        fieldsWithErrors={fieldsWithErrors}
      />
    );
  }

  return (
    <ErrorDefault
      className={className}
      fieldsWithErrors={fieldsWithErrors}
      locale={locale}
      errorKey={errorKey}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  const fieldsWithErrors = getInvalidParamErrors(state);
  return {
    fieldsWithErrors,
    locale: getCurrentLocale(state),
    hasDuplicatedPaymentError: hasDuplicatedPaymentParamError(state),
  };
};

const connector = connect(mapStateToProps);

export const ErrorComponent = connector(ErrorWrapper);
ErrorComponent.Default = ErrorDefault;
ErrorComponent.DuplicatedPayment = ErrorDuplicatedPayment;
